// src/utils/logger.js

/**
 * Centralized logging utility
 * Respects NODE_ENV to control logging in production
 * Sanitizes URLs and sensitive information for security
 */

const ENV = typeof import.meta !== "undefined" ? import.meta.env : process.env;
const isDevelopment = ENV?.NODE_ENV === 'development' || ENV?.MODE === 'development';

/**
 * Sanitize URL to remove sensitive information
 * Only shows endpoint path, not full URL
 */
function sanitizeUrl(url) {
    if (!url || typeof url !== 'string') return '[URL hidden]';
    
    // Extract just the endpoint path (e.g., /api/method/...)
    const match = url.match(/\/api\/method\/[^?]*/);
    if (match) {
        return match[0];
    }
    
    // If no API path found, return generic message
    return '[API endpoint]';
}

/**
 * Sanitize object to remove URLs and sensitive data
 */
function sanitizeData(data) {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = { ...data };
    
    // Remove URL fields
    if (sanitized.url) {
        sanitized.url = sanitizeUrl(sanitized.url);
    }
    if (sanitized.URL) {
        sanitized.URL = sanitizeUrl(sanitized.URL);
    }
    if (sanitized.endpoint) {
        sanitized.endpoint = sanitizeUrl(sanitized.endpoint);
    }
    
    // Remove sensitive headers
    if (sanitized.headers) {
        const safeHeaders = { ...sanitized.headers };
        delete safeHeaders.Authorization;
        delete safeHeaders['X-Frappe-CSRF-Token'];
        delete safeHeaders['x-frappe-csrf-token'];
        sanitized.headers = safeHeaders;
    }
    
    return sanitized;
}

export const logger = {
    debug: (...args) => {
        if (isDevelopment) {
            const sanitized = args.map(arg => 
                typeof arg === 'object' ? sanitizeData(arg) : arg
            );
            console.log('[DEBUG]', ...sanitized);
        }
    },

    info: (...args) => {
        if (isDevelopment) {
            const sanitized = args.map(arg => 
                typeof arg === 'object' ? sanitizeData(arg) : arg
            );
            console.info('[INFO]', ...sanitized);
        }
    },

    warn: (...args) => {
        if (isDevelopment) {
            const sanitized = args.map(arg => 
                typeof arg === 'object' ? sanitizeData(arg) : arg
            );
            console.warn('[WARN]', ...sanitized);
        }
    },

    error: (...args) => {
        // Always log errors, but sanitize URLs
        const sanitized = args.map(arg => {
            if (typeof arg === 'string' && arg.includes('http')) {
                return arg.replace(/https?:\/\/[^\s]+/g, '[URL hidden]');
            }
            return typeof arg === 'object' ? sanitizeData(arg) : arg;
        });
        console.error('[ERROR]', ...sanitized);
    },

    // For production-safe logging (e.g., analytics, monitoring)
    track: (event, data) => {
        if (isDevelopment) {
            console.log('[TRACK]', event, sanitizeData(data));
        }
        // In production, this could send to analytics service
    },
    
    // Helper to sanitize URLs in any context
    sanitizeUrl
};

export default logger;
